import { createGlobalStyle } from 'styled-components';

const GlobalStyles = createGlobalStyle`
  @import url('https://fonts.googleapis.com/css2?family=Outfit:wght@300;400;500;600;700;800;900&family=Cinzel:wght@600;700;800;900&display=swap');

  * {
    margin: 0;
    padding: 0;
    box-sizing: border-box;
  }

  body {
    font-family: 'Outfit', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif;
    font-size: clamp(13px, 2.5vw, 16px);
    line-height: 1.5;
    background: 
      radial-gradient(circle at 15% 25%, rgba(212, 175, 55, 0.008) 0%, transparent 50%),
      radial-gradient(circle at 85% 75%, rgba(212, 175, 55, 0.006) 0%, transparent 50%),
      radial-gradient(circle at 50% 50%, rgba(212, 175, 55, 0.004) 0%, transparent 70%),
      linear-gradient(135deg, var(--color-obsidian) 0%, var(--color-charcoal) 25%, var(--color-obsidian) 50%, var(--color-charcoal) 75%, var(--color-obsidian) 100%);
    color: var(--color-pearl);
    overflow-x: hidden;
    font-weight: 400;
    letter-spacing: -0.01em;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
    position: relative;
    
    /* Enhanced mobile scrolling */
    -webkit-overflow-scrolling: touch;
    
    @media (max-width: 375px) {
      font-size: clamp(12px, 3vw, 14px);
      line-height: 1.4;
    }
    
    @media (max-width: 320px) {
      font-size: clamp(11px, 3.5vw, 13px);
      line-height: 1.4;
    }
    
    &::before {
      content: '';
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: 
        radial-gradient(circle at 20% 30%, rgba(212, 175, 55, 0.003) 0%, transparent 60%),
        radial-gradient(circle at 80% 70%, rgba(212, 175, 55, 0.002) 0%, transparent 60%);
      pointer-events: none;
      z-index: -1;
      animation: luxury-ambient-glow 15s ease-in-out infinite;
    }
  }

  :root {
    /* Ultra-Premium Color Palette */
    --color-obsidian: #0B0B0B;
    --color-charcoal: #161616;
    --color-platinum: #F8F8F8;
    --color-pearl: #FEFEFE;
    --color-champagne: #D4AF37;
    --color-rose-gold: #E8B86D;
    --color-bronze: #CD7F32;
    --color-slate-900: #0F0F0F;
    --color-slate-800: #1A1A1A;
    --color-slate-700: #262626;
    --color-slate-600: #404040;
    --color-slate-500: #525252;
    --color-slate-400: #737373;
    --color-slate-300: #A3A3A3;
    
    /* Legacy mappings */
    --color-black: #0B0B0B;
    --color-white: #FEFEFE;
    --color-gold: #D4AF37;
    --color-gold-light: #E8B86D;
    --color-gold-dark: #CD7F32;
    --color-gray-900: #0F0F0F;
    --color-gray-800: #1A1A1A;
    --color-gray-700: #262626;
    --color-gray-600: #404040;
    --color-gray-500: #525252;
    --color-gray-400: #737373;
    --color-gray-300: #A3A3A3;

    /* Primary Colors */
    --primary: #FFD700;
    --primary-dark: #B8860B;
    --primary-light: #FFF700;
    
    /* Text Colors */
    --text-primary: #ffffff;
    --text-secondary: #9a9a9a;
    --text-muted: #7a7a7a;
    --text-subtle: #5a5a5a;
    
    /* Background Colors */
    --background-primary: #000000;
    --background-secondary: #111111;
    --background-tertiary: rgba(26, 26, 26, 0.8);
    --background-card: #1a1a1a;
    
    /* Border Colors */
    --border-primary: #FFD700;
    --border-secondary: rgba(255, 215, 0, 0.4);
    --border-subtle: rgba(255, 255, 255, 0.05);
    
    /* Shadow Colors */
    --shadow-primary: rgba(255, 215, 0, 0.15);
    --shadow-secondary: rgba(255, 215, 0, 0.1);
    --shadow-gold: rgba(255, 215, 0, 0.5);
    
    /* Gradients */
    --gradient-primary: linear-gradient(to bottom right, #FFD700, #FFF700, #FFD700);
    --gradient-gold: linear-gradient(45deg, #FFD700, #FFF700);
    --gradient-subtle: linear-gradient(135deg, rgba(255, 215, 0, 0.1) 0%, rgba(255, 215, 0, 0.05) 100%);
  }

  h1, h2, h3, h4, h5, h6 {
    font-weight: 600;
    color: var(--text-primary);
    letter-spacing: -0.02em;
    line-height: 1.1;
    margin: 0;
  }

  p {
    line-height: 1.5;
    color: var(--text-secondary);
    font-weight: 400;
    margin: 0;
  }

  button {
    cursor: pointer;
    border: none;
    outline: none;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    font-family: inherit;
    -webkit-font-smoothing: antialiased;
  }

  a {
    text-decoration: none;
    color: inherit;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  }

  /* Premium scrollbar */
  ::-webkit-scrollbar {
    width: 6px;
    
    @media (max-width: 768px) {
      width: 4px;
    }
  }

  ::-webkit-scrollbar-track {
    background: var(--background-secondary);
  }

  ::-webkit-scrollbar-thumb {
    background: var(--primary);
    border-radius: 3px;
  }

  ::-webkit-scrollbar-thumb:hover {
    background: var(--primary-dark);
  }
  
  /* Mobile viewport fixes */
  @media screen and (max-width: 768px) {
    html {
      -webkit-text-size-adjust: 100%;
    }
    
    * {
      -webkit-tap-highlight-color: transparent;
    }
  }

  /* Selection styling */
  ::selection {
    background: var(--primary);
    color: var(--background-primary);
  }

  /* Focus outline */
  *:focus-visible {
    outline: 2px solid var(--primary);
    outline-offset: 2px;
  }

  /* Premium Animations */
  @keyframes float {
    0%, 100% {
      transform: translateY(0px);
    }
    50% {
      transform: translateY(-8px);
    }
  }

  @keyframes glow-pulse {
    0%, 100% {
      box-shadow: 0 0 20px rgba(255, 215, 0, 0.3);
    }
    50% {
      box-shadow: 0 0 30px rgba(255, 215, 0, 0.6);
    }
  }

  @keyframes shimmer {
    0% {
      transform: translateX(-100%);
    }
    100% {
      transform: translateX(100%);
    }
  }

  /* Animation Classes */
  .animate-float {
    animation: float 4s ease-in-out infinite;
  }

  .animate-glow-pulse {
    animation: glow-pulse 2s ease-in-out infinite;
  }

  .animate-shimmer {
    animation: shimmer 3s infinite;
  }

  /* Ultra-Premium Typography Effects */
  .text-luxury-glow {
    text-shadow: 
      0 1px 2px rgba(11, 11, 11, 0.8),
      0 0 20px rgba(212, 175, 55, 0.15),
      0 0 40px rgba(212, 175, 55, 0.1);
  }

  .text-platinum {
    background: linear-gradient(135deg, #F8F8F8 0%, #FEFEFE 50%, #F8F8F8 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }

  .text-champagne-gradient {
    background: linear-gradient(135deg, #D4AF37 0%, #E8B86D 50%, #D4AF37 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }

  /* Luxury Spacing System */
  .luxury-spacing-xs { margin-bottom: 0.5rem; }
  .luxury-spacing-sm { margin-bottom: 0.75rem; }
  .luxury-spacing-md { margin-bottom: 1.25rem; }
  .luxury-spacing-lg { margin-bottom: 2rem; }
  .luxury-spacing-xl { margin-bottom: 2.5rem; }
  .luxury-spacing-2xl { margin-bottom: 3rem; }

  /* Premium Card Effects */
  .luxury-backdrop {
    backdrop-filter: blur(16px);
    -webkit-backdrop-filter: blur(16px);
    background: rgba(212, 175, 55, 0.03);
    border: 1px solid rgba(212, 175, 55, 0.1);
  }

  .luxury-hover-lift {
    transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  }

  .luxury-hover-lift:hover {
    transform: translateY(-8px);
    box-shadow: 0 20px 40px rgba(11, 11, 11, 0.3);
  }

  /* Ultra-Luxury Animations */
  @keyframes luxury-float {
    0%, 100% {
      transform: translateY(0px) scale(1);
    }
    50% {
      transform: translateY(-12px) scale(1.02);
    }
  }

  @keyframes luxury-pulse {
    0%, 100% {
      box-shadow: 0 0 20px rgba(212, 175, 55, 0.1);
    }
    50% {
      box-shadow: 0 0 40px rgba(212, 175, 55, 0.2);
    }
  }

  @keyframes luxury-glow {
    0%, 100% {
      text-shadow: 
        0 1px 2px rgba(11, 11, 11, 0.8),
        0 0 20px rgba(212, 175, 55, 0.1);
    }
    50% {
      text-shadow: 
        0 1px 2px rgba(11, 11, 11, 0.8),
        0 0 30px rgba(212, 175, 55, 0.2);
    }
  }

  /* Luxury Animation Classes */
  .luxury-float {
    animation: luxury-float 6s ease-in-out infinite;
  }

  .luxury-pulse {
    animation: luxury-pulse 3s ease-in-out infinite;
  }

  .luxury-glow {
    animation: luxury-glow 4s ease-in-out infinite;
  }

  /* Ultra-Premium Scroll Effects */
  .luxury-parallax {
    transform: translateZ(0);
    will-change: transform;
  }

  /* Sophisticated Border Effects */
  .luxury-border-gradient {
    position: relative;
    background: var(--color-obsidian);
    border-radius: 8px;
    
    &::before {
      content: '';
      position: absolute;
      top: -1px;
      left: -1px;
      right: -1px;
      bottom: -1px;
      background: linear-gradient(45deg, transparent, rgba(212, 175, 55, 0.2), transparent);
      border-radius: 8px;
      z-index: -1;
    }
  }

  /* Ultra-Premium Text Effects */
  @keyframes luxury-text-shimmer {
    0%, 100% {
      background-position: 0% 50%;
    }
    50% {
      background-position: 100% 50%;
    }
  }

  .luxury-text-shimmer {
    background: linear-gradient(135deg, #D4AF37 0%, #E8B86D 25%, #F8F8F8 50%, #E8B86D 75%, #D4AF37 100%);
    background-size: 200% 100%;
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    animation: luxury-text-shimmer 3s ease-in-out infinite;
  }

  /* Premium Button Sweep Effect */
  @keyframes luxury-button-sweep {
    0% { left: -100%; }
    100% { left: 100%; }
  }

  .luxury-button-sweep {
    position: relative;
    overflow: hidden;
    
    &::before {
      content: '';
      position: absolute;
      top: 0;
      left: -100%;
      width: 100%;
      height: 100%;
      background: linear-gradient(90deg, transparent, rgba(254, 254, 254, 0.2), transparent);
      transition: left 0.6s ease;
    }
    
    &:hover::before {
      animation: luxury-button-sweep 0.6s ease;
    }
  }

  /* Luxury Card Lift Effects */
  .luxury-card-lift {
    transition: all 0.6s cubic-bezier(0.4, 0, 0.2, 1);
    
    &:hover {
      transform: translateY(-12px) scale(1.02);
      box-shadow: 
        0 30px 60px rgba(212, 175, 55, 0.15),
        0 0 40px rgba(212, 175, 55, 0.05);
    }
  }

  /* Premium Backdrop Blur */
  .luxury-backdrop {
    backdrop-filter: blur(16px);
    -webkit-backdrop-filter: blur(16px);
  }

  /* Ultra-Sophisticated Input Focus */
  .luxury-input-focus {
    transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
    
    &:focus {
      transform: translateY(-2px);
      box-shadow: 0 8px 20px rgba(212, 175, 55, 0.1);
    }
  }

  /* Ultra-Luxury Ambient Glow Animation */
  @keyframes luxury-ambient-glow {
    0%, 100% {
      opacity: 0.3;
      transform: scale(1);
    }
    33% {
      opacity: 0.5;
      transform: scale(1.05);
    }
    66% {
      opacity: 0.4;
      transform: scale(0.95);
    }
  }

  /* Mind-Bending Floating Orbs */
  @keyframes luxury-orb-float {
    0%, 100% {
      transform: translateY(0px) translateX(0px) scale(1);
      opacity: 0.15;
    }
    25% {
      transform: translateY(-20px) translateX(10px) scale(1.1);
      opacity: 0.25;
    }
    50% {
      transform: translateY(-40px) translateX(-5px) scale(0.9);
      opacity: 0.2;
    }
    75% {
      transform: translateY(-20px) translateX(-15px) scale(1.05);
      opacity: 0.18;
    }
  }

  /* Ultra-Premium Section Dividers */
  .luxury-section-divider {
    position: relative;
    
    &::before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      height: 2px;
      background: linear-gradient(90deg, 
        transparent 0%, 
        rgba(212, 175, 55, 0.1) 10%, 
        rgba(212, 175, 55, 0.4) 25%, 
        var(--color-champagne) 50%, 
        rgba(212, 175, 55, 0.4) 75%, 
        rgba(212, 175, 55, 0.1) 90%, 
        transparent 100%
      );
      opacity: 0.8;
      animation: luxury-divider-pulse 4s ease-in-out infinite;
    }
    
    &::after {
      content: '';
      position: absolute;
      top: 0;
      left: 50%;
      transform: translateX(-50%);
      width: 60px;
      height: 6px;
      background: radial-gradient(ellipse at center, var(--color-champagne) 0%, transparent 70%);
      opacity: 0.6;
      animation: luxury-center-glow 3s ease-in-out infinite;
    }
  }

  @keyframes luxury-divider-pulse {
    0%, 100% { opacity: 0.6; }
    50% { opacity: 1; }
  }

  @keyframes luxury-center-glow {
    0%, 100% { 
      opacity: 0.4; 
      transform: translateX(-50%) scale(1);
    }
    50% { 
      opacity: 0.8; 
      transform: translateX(-50%) scale(1.2);
    }
  }

  /* Luxury Floating Elements */
  .luxury-floating-orb {
    position: absolute;
    border-radius: 50%;
    background: radial-gradient(circle at 30% 30%, rgba(212, 175, 55, 0.08), transparent 70%);
    backdrop-filter: blur(20px);
    animation: luxury-orb-float 12s ease-in-out infinite;
    pointer-events: none;
    z-index: 1;
  }

  .luxury-floating-orb:nth-child(1) {
    width: 200px;
    height: 200px;
    top: 10%;
    left: 5%;
    animation-delay: 0s;
  }

  .luxury-floating-orb:nth-child(2) {
    width: 150px;
    height: 150px;
    top: 60%;
    right: 8%;
    animation-delay: -4s;
  }

  .luxury-floating-orb:nth-child(3) {
    width: 100px;
    height: 100px;
    top: 30%;
    left: 80%;
    animation-delay: -8s;
  }

  /* Ultra-Premium Responsive Typography */
  .luxury-heading-mega {
    font-size: clamp(3rem, 8vw, 8rem);
    font-weight: 900;
    line-height: 0.9;
    letter-spacing: -0.04em;
  }

  .luxury-heading-xl {
    font-size: clamp(2.5rem, 6vw, 6rem);
    font-weight: 800;
    line-height: 1;
    letter-spacing: -0.03em;
  }

  .luxury-heading-lg {
    font-size: clamp(2rem, 5vw, 4rem);
    font-weight: 700;
    line-height: 1.1;
    letter-spacing: -0.02em;
  }

  .luxury-heading-md {
    font-size: clamp(1.5rem, 4vw, 2.5rem);
    font-weight: 600;
    line-height: 1.2;
    letter-spacing: -0.01em;
  }

  /* Mind-Bending Gradient Animations */
  @keyframes luxury-gradient-shift {
    0%, 100% {
      background-position: 0% 50%;
      filter: hue-rotate(0deg);
    }
    25% {
      background-position: 100% 50%;
      filter: hue-rotate(5deg);
    }
    50% {
      background-position: 100% 100%;
      filter: hue-rotate(0deg);
    }
    75% {
      background-position: 0% 100%;
      filter: hue-rotate(-5deg);
    }
  }

  .luxury-gradient-shift {
    background-size: 400% 400%;
    animation: luxury-gradient-shift 8s ease-in-out infinite;
  }

  /* Ultra-Luxury Parallax Effects */
  .luxury-parallax-slow {
    transform: translateZ(0);
    will-change: transform;
  }

  /* Missing Ultra-Premium Animations */
  @keyframes luxury-ripple {
    0% {
      transform: translate(-50%, -50%) scale(0);
      opacity: 1;
    }
    100% {
      transform: translate(-50%, -50%) scale(2);
      opacity: 0;
    }
  }

  @keyframes luxury-text-aura {
    0%, 100% {
      opacity: 0.6;
      filter: blur(1rem);
    }
    33% {
      opacity: 0.9;
      filter: blur(1.5rem);
    }
    66% {
      opacity: 0.7;
      filter: blur(0.8rem);
    }
  }

  @keyframes luxury-content-glow {
    0%, 100% {
      opacity: 0.8;
      transform: scale(1);
    }
    50% {
      opacity: 1;
      transform: scale(1.05);
    }
  }

  /* Responsive Container System */
  .luxury-container {
    max-width: 1600px;
    margin: 0 auto;
    padding: 0 2rem;
    
    @media (max-width: 1400px) {
      max-width: 1200px;
      padding: 0 1.5rem;
    }
    
    @media (max-width: 768px) {
      padding: 0 1rem;
    }
  }

  /* Ultra-Premium Grid System */
  .luxury-grid {
    display: grid;
    gap: 3rem;
    
    @media (max-width: 968px) {
      gap: 2rem;
    }
    
    @media (max-width: 768px) {
      gap: 1.5rem;
    }
  }

  .luxury-grid-2 {
    grid-template-columns: repeat(2, 1fr);
    
    @media (max-width: 768px) {
      grid-template-columns: 1fr;
    }
  }

  .luxury-grid-3 {
    grid-template-columns: repeat(3, 1fr);
    
    @media (max-width: 968px) {
      grid-template-columns: repeat(2, 1fr);
    }
    
    @media (max-width: 768px) {
      grid-template-columns: 1fr;
    }
  }

  .luxury-grid-4 {
    grid-template-columns: repeat(4, 1fr);
    
    @media (max-width: 1200px) {
      grid-template-columns: repeat(3, 1fr);
    }
    
    @media (max-width: 968px) {
      grid-template-columns: repeat(2, 1fr);
    }
    
    @media (max-width: 768px) {
      grid-template-columns: 1fr;
    }
  }

  /* Ultra-Sophisticated Button System */
  .luxury-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 1.5rem 3rem;
    font-weight: 700;
    font-size: 1rem;
    letter-spacing: 0.05em;
    text-transform: uppercase;
    border-radius: 12px;
    transition: all 0.6s cubic-bezier(0.4, 0, 0.2, 1);
    position: relative;
    overflow: hidden;
    backdrop-filter: blur(16px);
    cursor: pointer;
    border: none;
    
    &::before {
      content: '';
      position: absolute;
      top: 0;
      left: -100%;
      width: 100%;
      height: 100%;
      background: linear-gradient(90deg, transparent, rgba(254, 254, 254, 0.2), transparent);
      transition: left 0.8s ease;
    }
    
    &:hover::before {
      left: 100%;
    }
  }

  .luxury-btn-primary {
    background: var(--color-champagne);
    color: var(--color-obsidian);
    border: 1px solid var(--color-champagne);
    box-shadow: 0 8px 20px rgba(212, 175, 55, 0.15);
    
    &:hover {
      background: var(--color-obsidian);
      color: var(--color-champagne);
      transform: translateY(-4px) scale(1.02);
      box-shadow: 0 20px 40px rgba(212, 175, 55, 0.3);
    }
  }

  .luxury-btn-secondary {
    background: rgba(212, 175, 55, 0.08);
    color: var(--color-champagne);
    border: 1px solid rgba(212, 175, 55, 0.3);
    
    &:hover {
      background: rgba(212, 175, 55, 0.15);
      transform: translateY(-4px) scale(1.02);
      box-shadow: 0 20px 40px rgba(212, 175, 55, 0.2);
    }
  }

  /* Ultra-Premium Loading Effects */
  @keyframes luxury-loading-pulse {
    0%, 100% {
      opacity: 0.4;
      transform: scale(1);
    }
    50% {
      opacity: 1;
      transform: scale(1.05);
    }
  }

  .luxury-loading {
    animation: luxury-loading-pulse 2s ease-in-out infinite;
  }
`;

export default GlobalStyles;